import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ResetPasswordForm } from '../../organisms';
import { useResetPassword } from '@/features/auth/hooks/useResetPassword';
import { useAuthStore } from '@/features/auth/store/auth';
import { getFederatedLoginRequired } from '@/shared/utils/apiError';
import type { FederatedLoginLocationState } from '@/features/auth/types/auth.types';
import i18n from '@/shared/i18n';
import { hasApiErrorCode } from '@/shared/utils/apiError';
import {
  PASSWORD_ERROR_CODES,
  PASSWORD_ALREADY_USED_MESSAGE_KEY,
} from '@/features/auth/utils/passwordErrorCodes';

const ResetPasswordPage = () => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setError: setAuthError } = useAuthStore();

  const username = searchParams.get('username');
  const otp = searchParams.get('validationCode');

  useEffect(() => {
    setAuthError(null);

    // Validate URL parameters on mount
    if (!username || !otp) {
      setError(i18n.t('Invalid reset link. Email or OTP is missing.'));
    }
  }, [setAuthError, username, otp]);

  const { mutate: resetPasswordMutate, isPending } = useResetPassword({
    onSuccess: () => {
      setSuccess(true);
      setError(null);
      setAuthError(null);
    },
    onError: (err) => {
      // RN04/CA06 — usuário de cliente federado chegando por link de
      // redefinição antigo (e-mail enviado antes da ativação da federação, ou
      // favorito): nenhuma credencial é alterada e o usuário é conduzido ao
      // fluxo corporativo com o e-mail preservado, em vez de ver um erro
      // genérico que não explica a mudança.
      const federated = getFederatedLoginRequired(err);
      if (federated) {
        const state: FederatedLoginLocationState = {
          email: username ?? undefined,
          reason: 'password-recovery-blocked',
          registrationId: federated.registrationId,
          authenticationUrl: federated.authenticationUrl,
        };
        navigate('/sso', { state, replace: true });
        return;
      }

      const errorMessage = hasApiErrorCode(
        err,
        PASSWORD_ERROR_CODES.ALREADY_USED
      )
        ? i18n.t(PASSWORD_ALREADY_USED_MESSAGE_KEY)
        : err.message || i18n.t('Failed to reset password');
      setError(errorMessage);
      setSuccess(false);
    },
  });

  const handleResetPassword = async (data: { newPassword: string }) => {
    if (!username || !otp) {
      setError(i18n.t('Invalid reset link. Email or OTP is missing.'));
      return;
    }

    resetPasswordMutate({
      username,
      newPassword: data.newPassword,
      otp,
    });
  };

  return (
    <ResetPasswordForm
      onSubmit={handleResetPassword}
      loading={isPending}
      error={error}
      success={success}
    />
  );
};

export default ResetPasswordPage;
